import {Vertical} from "../../../common/ts/types/Vertical";

const LEGACY_COOKIE_NAME: string = 'visited';
const COOKIE_NAME: string = 'visited-listings';
const COOKIE_EXPIRATION_DAYS: number = 90;
const MAX_VISITED_LISTINGS: number = 30;

export interface LegacyVisitedListing {
    id: string;
    isPremium: boolean;
    modalHasBeenSeen: boolean;
}

export interface VisitedListing {
    id: string; // id
    m: boolean; // modalHasBeenSeen
}

export class VisitedCookie {

    constructor() {
        if (!this.doesCookieExist(COOKIE_NAME)) {
            this.migrateLegacyVisitedCookie();
        }
    }

    public addVisitedListing(listing: VisitedListing) {
        let visitedListings: VisitedListing[] = this.getVisitedListings();
        const index: number = visitedListings.findIndex((visitedListing: VisitedListing) =>
            visitedListing.id === listing.id
        );
        if (index === -1) {
            visitedListings.push(listing);
            this.setVisitedCookie(visitedListings);
        }
    }

    public isVisited(listingId: string): boolean {
        const visitedListings: VisitedListing[] = this.getVisitedListings();
        return visitedListings.some((visitedListing: VisitedListing) =>
            visitedListing.id === listingId
        );
    }

    public theModalHasBeenSeen(listingId: string): boolean {
        const visitedListings: VisitedListing[] = this.getVisitedListings();
        return visitedListings.some((visitedListing: VisitedListing) =>
            visitedListing.id === listingId && visitedListing.m
        );
    }

    public setModalHasBeenSeen(listingId: string) {
        const visitedListings: VisitedListing[] = this.getVisitedListings();
        const index: number = visitedListings.findIndex((visitedListing: VisitedListing) =>
            visitedListing.id === listingId
        );
        if (index !== -1) {
            visitedListings[index].m = true;
            this.setVisitedCookie(visitedListings);
        }
    }

    public getVisitedListings(): VisitedListing[] {
        const parsedValue = this.getParsedCookie<any>(COOKIE_NAME);
        return parsedValue?.[Vertical.HOMES] || [];
    }

    private getParsedCookie<T>(cookieName: string): T | null {
        const cookieList: string[] = document.cookie.split(';');
        const cookie = cookieList.find(c => c.trim().startsWith(cookieName));
        if (!cookie) return null;
        const value: string = cookie.trim().substring(cookieName.length + 1);
        try {
            return JSON.parse(decodeURIComponent(value));
        } catch {
            return null;
        }
    }

    private doesCookieExist(cookieName: string): boolean {
        return this.getParsedCookie<any>(cookieName) !== null;
    }

    private setVisitedCookie(listings: VisitedListing[]) {
        // Ensure we only keep the most recent MAX_VISITED_LISTINGS
        const trimmedListings = listings.slice(-MAX_VISITED_LISTINGS);
        const value: string = JSON.stringify({
            id: COOKIE_NAME,
            [Vertical.HOMES]: trimmedListings
        });
        const expirationTimeInMs: number = COOKIE_EXPIRATION_DAYS * 24 * 60 * 60 * 1000;
        const date: Date = new Date();
        date.setTime(date.getTime() + expirationTimeInMs);
        const expires: string = `expires=${date.toUTCString()}`;
        const htmlEncodedValue: string = encodeURIComponent(value);
        document.cookie = `${COOKIE_NAME}=${htmlEncodedValue}; ${expires}; Path=/`;
    }

    private migrateLegacyVisitedCookie() {
        const legacyVisitedListings: LegacyVisitedListing[] = this.getLegacyVisitedListings();
        if (!Array.isArray(legacyVisitedListings) || legacyVisitedListings.length === 0) return;
        const migrated = legacyVisitedListings
            .map((l: LegacyVisitedListing) => this.toVisitedListing(l));
        this.setVisitedCookie(migrated);
        this.removeLegacyCookie();
    }

    private removeLegacyCookie() {
        document.cookie = `${LEGACY_COOKIE_NAME}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; Path=/;`;
    }

    private getLegacyVisitedListings(): LegacyVisitedListing[] {
        const parsedValue = this.getParsedCookie<any>(LEGACY_COOKIE_NAME);
        return parsedValue?.[Vertical.HOMES] || [];
    }

    private toVisitedListing(listing: LegacyVisitedListing): VisitedListing {
        return {id: listing.id, m: listing.modalHasBeenSeen};
    }
}
